/**
 * Turns a FilterSelection into (a) the `ProductFilter[]` pushed to the Storefront
 * API and (b) the tag groups AND-ed on the client (Tier 2).
 *
 * Rules encoded here (see docs/storefront-filtering.md):
 *  - Native groups (vendor / productType / variantOption): all pushed to the API.
 *    Multiple values in one group OR; different groups AND — both handled natively.
 *  - Price is the exception: the API silently honours only the FIRST `price` input and
 *    drops the rest, so multiple buckets are collapsed into one enclosing range and the
 *    exact union is resolved client-side (`priceRanges`).
 *  - Tag groups: at most ONE can be pushed to the API (multiple `tag` inputs OR,
 *    so pushing two different tag groups would wrongly OR them). We push the single
 *    highest-priority / most-selective tag group and refine the rest on the client.
 */

import {
  PRICE_RANGES,
  getGroup,
  nativeFiltersForValue,
  tagForValue,
} from './FilterConfig';
import {
  BuiltFilters,
  ClientRefinement,
  FilterGroupConfig,
  FilterSelection,
  PriceRange,
  ProductFilterInput,
} from './types';

/**
 * Collapse selected buckets into the fewest disjoint intervals covering them, merging
 * ones that overlap or touch ($25-50 + $50-100 -> a single 25..100). A product's min
 * price falls in exactly one merged interval, so counting them separately never
 * double-counts.
 */
export function mergePriceRanges(ranges: PriceRange[]): PriceRange[] {
  if (ranges.length === 0) return [];
  const sorted = ranges
    .map((r) => ({
      min: typeof r.min === 'number' ? r.min : 0,
      max: typeof r.max === 'number' ? r.max : Infinity,
    }))
    .sort((a, b) => a.min - b.min);

  const merged: { min: number; max: number }[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].min <= last.max) {
      if (sorted[i].max > last.max) last.max = sorted[i].max;
    } else {
      merged.push(sorted[i]);
    }
  }

  return merged.map((m) => {
    const out: PriceRange = { min: m.min };
    if (m.max !== Infinity) out.max = m.max;
    return out;
  });
}

export function priceRangeToFilter(range: PriceRange): ProductFilterInput {
  const price: { min?: number; max?: number } = {};
  if (typeof range.min === 'number') price.min = range.min;
  if (typeof range.max === 'number') price.max = range.max;
  return { price };
}

export function isPriceFilter(filter: ProductFilterInput): boolean {
  return (filter as { price?: unknown }).price !== undefined;
}

/**
 * Multiple `price` inputs do NOT OR: the Storefront API applies the first one and
 * ignores every later one (verified against the live API), so two buckets returned
 * exactly the first bucket's result set. Push a single range spanning every selected
 * bucket instead — a superset of their union, which `priceInAnyRange` then narrows to
 * the exact selection on the client.
 */
function priceEnvelope(ranges: PriceRange[]): ProductFilterInput | null {
  const merged = mergePriceRanges(ranges);
  if (merged.length === 0) return null;
  if (merged.length === 1) return priceRangeToFilter(merged[0]);
  const last = merged[merged.length - 1];
  const envelope: PriceRange = { min: merged[0].min };
  if (typeof last.max === 'number') envelope.max = last.max;
  return priceRangeToFilter(envelope);
}

interface ActiveTagGroup {
  group: FilterGroupConfig;
  values: string[];
  tags: string[];
}

function pickPrimaryTagGroup(active: ActiveTagGroup[]): ActiveTagGroup | null {
  if (active.length === 0) return null;
  return active.slice().sort((a, b) => {
    if (a.values.length !== b.values.length) return a.values.length - b.values.length;
    return (a.group.primaryRank || 0) - (b.group.primaryRank || 0);
  })[0];
}

export function buildFilters(selection: FilterSelection): BuiltFilters {
  const apiFilters: ProductFilterInput[] = [];
  const activeTagGroups: ActiveTagGroup[] = [];
  const priceRanges: PriceRange[] = [];

  Object.keys(selection || {}).forEach((key) => {
    const values = (selection[key] || []).filter(Boolean);
    if (values.length === 0) return;
    const group = getGroup(key);
    if (!group) return;

    if (group.type === 'tag') {
      activeTagGroups.push({
        group,
        values,
        tags: values.map((v) => tagForValue(group, v)),
      });
    } else if (group.type === 'price') {
      values.forEach((v) => {
        if (PRICE_RANGES[v]) priceRanges.push(PRICE_RANGES[v]);
      });
    } else {
      values.forEach((v) => {
        nativeFiltersForValue(group, v).forEach((f) => apiFilters.push(f));
      });
    }
  });

  const envelope = priceEnvelope(priceRanges);
  if (envelope) apiFilters.push(envelope);

  const primary = pickPrimaryTagGroup(activeTagGroups);
  if (primary) {
    primary.tags.forEach((tag) => apiFilters.push({ tag }));
  }

  const clientRefinements: ClientRefinement[] = activeTagGroups
    .filter((t) => t !== primary)
    .map((t) => ({ key: t.group.key, tags: t.tags }));

  return { apiFilters, clientRefinements, priceRanges };
}

export function productMatchesRefinements(
  productTags: string[],
  refinements: ClientRefinement[]
): boolean {
  if (refinements.length === 0) return true;
  const tagSet: Record<string, true> = {};
  productTags.forEach((t) => {
    tagSet[t] = true;
  });
  return refinements.every((r) => r.tags.some((t) => tagSet[t] === true));
}
